import {
  HIDDEN_SELECTOR,
  deactivate,
  isActive,
  isCapturable,
  isCloseKey,
  isDownKey,
  isSelectionKey,
  isShiftableKey,
  isVerticalArrowKey,
  isVerticalMovementKey,
  updateElementFocus,
} from '../../../../../utils/keyboard-nav-support';
import { indexInRange } from '../../../../../utils/spds/array-handling';
import { tracked } from '@glimmer/tracking';
import { waitForLater, waitForNext } from '../../../../../utils/waiters';
import Super from '../../../core/base.gjs';

const MENU_OPEN_DELAY = 100;
const ALL_OPTIONS = 'all-options';

export default class SpdsActionButtonFilterSingleArrowNavigation extends Super {
  defaultOption = ALL_OPTIONS;

  @tracked _value;
  @tracked menuNavigableOptions = [];

  get menuCanOpen() {
    return this.menuNavigableOptions.length > 0;
  }

  get navigableOptions() {
    return this.menuNavigableOptions.filter(el => {
      let element = this.isMultiselect ? el.parentElement : el;
      return !element.classList.contains(HIDDEN_SELECTOR);
    });
  }

  get value() {
    return this._value ?? this.defaultOption;
  }

  set value(value) {
    this._value = value;
  }

  constructor() {
    super(...arguments);

    this.value = this.args.selectedOption;
  }

  willDestroy() {
    super.willDestroy(...arguments);

    this.baseElement.removeEventListener('keydown', this.keyboardNav);
  }

  canTriggerKeyboardOpen(key) {
    return (isVerticalArrowKey(key) || isSelectionKey(key)) && (this.menuCanOpen ?? true);
  }

  determineDirection({ key, shiftKey }) {
    return isDownKey(key) && !(shiftKey && isShiftableKey(key)) ? 1 : -1;
  }

  findOptionByValue(value) {
    return this.navigableOptions.find(option => option.dataset.value === value);
  }

  getActiveOption() {
    return this.navigableOptions?.find(isActive);
  }

  handleOpen = () => {
    this.open = true;
  };

  handleVerticalPress(evt) {
    let activeElement = this.getActiveOption();
    let modifier = this.determineDirection(evt);
    let startingIndex = this.navigableOptions.findIndex(option => option === activeElement);

    if (indexInRange(this.navigableOptions.length, startingIndex)) {
      deactivate(this.navigableOptions[startingIndex]);
    }

    this.updateVerticalSelection(startingIndex + modifier);
  }

  isCurrentValue(value) {
    return value === this.currentValue;
  }

  keyboardNav = evt => {
    if (this.args.disabled) return;

    if (!this.open) {
      this.openWithKeyboard(evt);
    } else {
      this.parseKeyAction(evt);
    }
  };

  manageSelectedItem(evt) {
    if (isVerticalMovementKey(evt.key)) {
      this.handleVerticalPress(evt);
    }
  }

  removeFocus() {
    this.menuNavigableOptions.forEach(option => {
      option.classList.remove('active');
    });
  }

  onClose = () => {
    this.open = false;
    this.removeFocus();
  };

  async openWithKeyboard(evt) {
    if (!this.canTriggerKeyboardOpen(evt.key)) return;

    evt.preventDefault();
    this.handleOpen();

    if (!isSelectionKey(evt.key)) {
      await waitForLater(MENU_OPEN_DELAY);

      this.manageSelectedItem(evt);
    }
  }

  parseKeyAction(evt) {
    let { key } = evt;

    if (!isCapturable(key)) return;

    evt.preventDefault();

    if (isSelectionKey(key)) {
      this.getActiveOption().click();
    } else if (isCloseKey(key)) {
      this.onClose();
    } else {
      this.manageSelectedItem(evt);
    }
  }

  setSelection(value) {
    if (this.isCurrentValue(value)) return;

    let currentSelection = this.findOptionByValue(this.currentValue);

    currentSelection?.setAttribute('aria-selected', 'false');

    this.value = value;
    this.currentValue = value;

    this.selected = this.findOptionByValue(value);

    this.selected?.setAttribute('aria-selected', 'true');
  }

  setup = async () => {
    let selectedOption = this.args.selectedOption ?? 'all-options';

    await waitForNext();
    this.menuNavigableOptions = this.menuComponent.navigableOptions ?? [];
    this.setSelection(selectedOption);

    this.baseElement.addEventListener('keydown', this.keyboardNav);
  };

  updateVerticalSelection(index) {
    let lastIndex = this.navigableOptions.length - 1;

    if (index < 0) {
      this.updateFocus(lastIndex);
    } else if (!indexInRange(this.navigableOptions.length, index)) {
      this.updateFocus(0);
    } else {
      this.updateFocus(index);
    }
  }

  updateFocus(index) {
    this.activeOption = this.navigableOptions[index];
    updateElementFocus(this.activeOption);
  }
}
